import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { portalsDataKey } from 'src/app/core/constants/constants';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { accountHasAddon } from 'src/app/core/utils/global-utils';
import { CustomerPortal } from '../../../secured-skeleton/models/customer-portal';
import { environment } from 'src/environments/environment';
import { finalize, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { shopCrossSelling, shopForbidden } from 'src/app/core/constants/routes';
import { trigger, state, style, animate, transition, AnimationBuilder, AnimationPlayer } from '@angular/animations';

@Component({
  selector: 'welcome',
  templateUrl: './welcome.component.html',
  styleUrls: ['./welcome.component.scss'],
  standalone: false,
})
export class WelcomeComponent implements OnInit {
  // Stores all the portals information
  customerPortals: CustomerPortal[];

  // Stores the portal showed in the carousel
  carouselPortals: CustomerPortal[];

  // Stores the indeces to handle the carousel
  startIndex: number = 0;
  lastIndex: number = 5;

  /** True while the create redirect token is being created */
  isCreatingRedirectToken = false;

  carouselInterval: any;

  constructor(
    private router: Router,
    private userDataService: UserDataService,
    private http: HttpClient,
    private animationBuilder: AnimationBuilder,
    private permissionsService: PermissionsService,
  ) {}

  ngOnInit(): void {
    this.carouselInterval = setInterval(() => this.rightClick(), 5000);
    this.customerPortals = JSON.parse(sessionStorage.getItem(portalsDataKey)).filter((portal) => portal.isActive);
    this.carouselPortals = [...this.customerPortals];
  }

  private resetInterval() {
    clearInterval(this.carouselInterval);
    this.carouselInterval = setInterval(() => this.rightClick(), 5000);
  }

  leftClick() {
    if (this.startIndex === 0) {
      this.startIndex = this.customerPortals.length - 1;
      this.lastIndex--;
      this.carouselPortals.unshift(this.customerPortals[this.customerPortals.length - 1]);
      this.carouselPortals.pop();
    } else if (this.lastIndex === 0) {
      this.lastIndex = this.customerPortals.length - 1;
      this.startIndex--;
      this.carouselPortals.unshift(this.customerPortals[this.startIndex]);
      this.carouselPortals.pop();
    } else {
      this.startIndex--;
      this.lastIndex--;
      this.carouselPortals.unshift(this.customerPortals[this.startIndex]);
      this.carouselPortals.pop();
    }
    this.playSlideAnimation('left');
    this.resetInterval();
    return;
  }

  rightClick() {
    if (this.lastIndex === this.customerPortals.length - 1) {
      this.lastIndex = 0;
      this.startIndex++;
      this.carouselPortals.shift();
      this.carouselPortals.push(this.customerPortals[0]);
    } else if (this.startIndex === this.customerPortals.length - 1) {
      this.startIndex = 0;
      this.lastIndex++;
      this.carouselPortals.shift();
      this.carouselPortals.push(this.customerPortals[this.lastIndex]);
    } else {
      this.startIndex++;
      this.lastIndex++;
      this.carouselPortals.shift();
      this.carouselPortals.push(this.customerPortals[this.lastIndex]);
    }
    this.playSlideAnimation('right');
    this.resetInterval();
    return;
  }

  private redirectToShop() {
    this.isCreatingRedirectToken = true;
    this.createRedirectToken()
      .pipe(finalize(() => (this.isCreatingRedirectToken = false)))
      .subscribe((tokenResult) => {
        window.open(
          `${environment.redirects['xchange']}${environment.genXchangeToken ? `?redirect-token=${tokenResult.token}` : ''}`,
          '_blank',
        );
      });
  }

  private playSlideAnimation(direction: 'right' | 'left') {
    const slideAnimation = this.animationBuilder.build([
      style({ transform: `translateX(${direction === 'right' ? '100%' : '-100%'})` }),
      animate('500ms ease-in-out', style({ transform: `translateX(0%)` })),
    ]);
    const slides = document.querySelectorAll('.carousel__content--slide');
    slides.forEach((slide) => {
      const player = slideAnimation.create(slide);
      player.play();
    });
  }

  private createRedirectToken(): Observable<{ token: string }> {
    const apiUri = `${environment.restUrl}/v1/integrations/shop/redirect-token`;

    return this.http.post<{ token: string }>(apiUri, {
      responseType: 'json',
    });
  }

  /** Called when the shop option is clicked */
  xchangeClicked() {
    const companyHasAddon = accountHasAddon('shop', this.userDataService.company?.serviceAddons ?? []);

    if (!companyHasAddon) {
      this.router.navigateByUrl(shopCrossSelling);
      return;
    }

    if (this.permissionsService.hasPermission('shop', 'Shop', 'view')) {
      if (!this.isCreatingRedirectToken) {
        // Guard against double clicks
        this.redirectToShop();
      }
    } else {
      this.router.navigateByUrl(shopForbidden);
    }
  }

  onCustomerPortalClicked(portal: CustomerPortal) {
    if (!this.userDataService.company.serviceAddons.includes(portal.addonName)) {
      this.router.navigateByUrl(`/secured/cross-selling/${portal.addonName}`);
      return;
    } else if (!this.permissionsService.hasAddonAccess(portal.addonName)) {
      this.router.navigateByUrl(`/secured/forbidden/${portal.addonName}`);
      return;
    }

    if (portal.name === `Shop`) {
      // shop is an exception

      this.xchangeClicked();
    } else if (portal.isExternal) {
      if (portal.sections.length > 0) {
        const fullUrl = this.createPath(portal.baseUrl, portal.sections[0].subdomain, portal.sections[0].uri);
        window.open(fullUrl, '_blank');
      } else {
        window.open(portal.baseUrl, '_blank');
      }
    } else {
      // TODO: Expand the functionality to multiple sections
      if (portal.sections.length === 1) {
        const fullUrl = this.createPath(portal.baseUrl, portal.sections[0].subdomain, portal.sections[0].uri);
        this.router.navigateByUrl(fullUrl);
      } else {
        this.router.navigateByUrl(portal.baseUrl);
      }
    }
  }

  private createPath(baseUrl: string, subdomain: string, uri: string): string {
    let path = baseUrl;

    /** If subdomain has a value append this at the beginning  */
    if (subdomain) {
      path = baseUrl.replace('https://', `https://${subdomain}.`);
    }

    /** If uri has a value append this at the end */
    if (uri) {
      path += uri;
    }

    return path;
  }
}
